import React, { useState, useMemo } from 'react';
import { Send, Mail, MessageSquare, ChevronDown, ChevronUp, Download, Users, Trash2, Loader2, MailCheck, User, Search } from 'lucide-react';

const CampaignsPage = ({
  campaigns,
  templates,
  contacts,
  selectedContacts,
  selectedContactsDetails,
  settings,
  campaignForm,
  setCampaignForm,
  createCampaign,
  sendTestEmail,
  sendTestSms,
  sendSingleEmail,
  sendSingleSms,
  testEmailSending,
  testSmsSending,
  expandedCampaign,
  setExpandedCampaign,
  exportCampaignReport,
  setCurrentPage,
  deleteCampaign,
  campaignDeletingId
}) => {
  const [singleMessageForm, setSingleMessageForm] = useState({
    channel: 'email',
    templateId: null,
    contactId: null,
    email: '',
    phone: '',
    searchTerm: ''
  });
  const [sendingSingleMessage, setSendingSingleMessage] = useState(false);
  const [useDirectRecipient, setUseDirectRecipient] = useState(true);

  const singleChannel = singleMessageForm.channel;
  const availableSingleTemplates = useMemo(
    () => templates.filter((template) => template.type === singleChannel),
    [templates, singleChannel]
  );

  // Filter contacts for single message search
  const filteredContacts = useMemo(() => {
    if (!singleMessageForm.searchTerm || useDirectRecipient) return [];
    const search = singleMessageForm.searchTerm.toLowerCase();
    return contacts
      .filter((contact) => {
        const fullName = `${contact.firstName || contact.first_name || ''} ${contact.lastName || contact.last_name || ''}`.trim().toLowerCase();
        const searchablePrimary = singleChannel === 'email' ? contact.email : contact.phone;
        if (!searchablePrimary) return false;
        return (
          searchablePrimary.toLowerCase().includes(search) ||
          fullName.includes(search)
        );
      })
      .slice(0, 10); // Limit to 10 results
  }, [contacts, singleChannel, singleMessageForm.searchTerm, useDirectRecipient]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits.length >= 10;
  };

  const handleSingleChannelChange = (channel) => {
    setSingleMessageForm({
      channel,
      templateId: null,
      contactId: null,
      email: '',
      phone: '',
      searchTerm: ''
    });
    setUseDirectRecipient(true);
  };

  const handleSendSingleMessage = async () => {
    if (!singleMessageForm.templateId) {
      return;
    }

    if (useDirectRecipient) {
      if (singleChannel === 'email' && (!singleMessageForm.email || !isValidEmail(singleMessageForm.email))) {
        return;
      }
      if (singleChannel === 'sms' && (!singleMessageForm.phone || !isValidPhone(singleMessageForm.phone))) {
        return;
      }
    } else {
      if (!singleMessageForm.contactId) {
        return;
      }
    }

    setSendingSingleMessage(true);
    try {
      if (singleChannel === 'email') {
        await sendSingleEmail(
          singleMessageForm.templateId,
          useDirectRecipient ? null : singleMessageForm.contactId,
          useDirectRecipient ? singleMessageForm.email : null
        );
      } else {
        await sendSingleSms(
          singleMessageForm.templateId,
          useDirectRecipient ? null : singleMessageForm.contactId,
          useDirectRecipient ? singleMessageForm.phone : null
        );
      }
      setSingleMessageForm((prev) => ({
        channel: prev.channel,
        templateId: null,
        contactId: null,
        email: '',
        phone: '',
        searchTerm: ''
      }));
    } finally {
      setSendingSingleMessage(false);
    }
  };

  const handleSendSingleTest = async () => {
    if (singleChannel !== 'sms' || !singleMessageForm.templateId) {
      return;
    }

    if (useDirectRecipient) {
      if (!singleMessageForm.phone || !isValidPhone(singleMessageForm.phone)) {
        return;
      }
    } else if (!singleMessageForm.contactId) {
      return;
    }

    await sendTestSms(
      singleMessageForm.templateId,
      useDirectRecipient ? null : singleMessageForm.contactId,
      useDirectRecipient ? singleMessageForm.phone : null
    );
  };

  const selectedContact = contacts.find(c => c.id === singleMessageForm.contactId);
  const selectedRecipientList = useMemo(
    () => Array.from(selectedContacts).map((id) => selectedContactsDetails.get(id)).filter(Boolean),
    [selectedContacts, selectedContactsDetails]
  );
  const emailReadyCount = useMemo(
    () => selectedRecipientList.filter((contact) => Boolean(contact?.email)).length,
    [selectedRecipientList]
  );
  const smsReadyCount = useMemo(
    () => selectedRecipientList.filter((contact) => Boolean(contact?.phone)).length,
    [selectedRecipientList]
  );
  const bothReadyCount = useMemo(
    () => selectedRecipientList.filter((contact) => Boolean(contact?.email) && Boolean(contact?.phone)).length,
    [selectedRecipientList]
  );
  const availableRecipientCount = useMemo(() => {
    if (campaignForm.channel === 'email') return emailReadyCount;
    if (campaignForm.channel === 'sms') return smsReadyCount;
    return selectedRecipientList.filter((contact) => Boolean(contact?.email) || Boolean(contact?.phone)).length;
  }, [campaignForm.channel, emailReadyCount, smsReadyCount, selectedRecipientList]);
  const unavailableRecipientCount = Math.max(0, selectedContacts.size - availableRecipientCount);
  const smsReady = Boolean(settings?.credentials?.smsReady);
  const activeCampaigns = campaigns.filter((campaign) => campaign.status !== 'completed');
  const queuedCount = activeCampaigns.filter((c) => c.status === 'queued').length;
  const inProgressCount = activeCampaigns.filter((c) => c.status === 'in_progress').length;
  const scheduledCount = activeCampaigns.filter((c) => c.status === 'scheduled').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-600 mt-1">Create and manage your marketing campaigns</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign List */}
        <div className="lg:col-span-2 space-y-6">
          {/* Send Single Message Section */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${singleChannel === 'email' ? 'bg-indigo-100' : 'bg-purple-100'}`}>
                {singleChannel === 'email'
                  ? <User className="text-indigo-600" size={20} />
                  : <MessageSquare className="text-purple-600" size={20} />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Send Single Message</h2>
                <p className="text-sm text-gray-600">
                  Send a one-off {singleChannel === 'email' ? 'email' : 'SMS'} to a direct recipient or existing contact
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Channel *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleSingleChannelChange('email')}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                      singleChannel === 'email' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSingleChannelChange('sms')}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                      singleChannel === 'sms' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    SMS
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Template *</label>
                <select
                  value={singleMessageForm.templateId || ''}
                  onChange={(e) => setSingleMessageForm({ ...singleMessageForm, templateId: e.target.value || null })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="">Select a template</option>
                  {availableSingleTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}{template.type === 'email' && (template.fromName || template.fromEmail) ? ` · ${template.fromName || template.fromEmail}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center gap-4 mb-2">
                  <label className="block text-sm font-semibold text-gray-700">Recipient *</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setUseDirectRecipient(true);
                        setSingleMessageForm({
                          ...singleMessageForm,
                          contactId: null,
                          searchTerm: ''
                        });
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                        useDirectRecipient 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Enter {singleChannel === 'email' ? 'Email' : 'Phone'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUseDirectRecipient(false);
                        setSingleMessageForm({
                          ...singleMessageForm,
                          email: '',
                          phone: ''
                        });
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                        !useDirectRecipient 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Search Contact
                    </button>
                  </div>
                </div>

                {useDirectRecipient ? (
                  <input
                    type={singleChannel === 'email' ? 'email' : 'tel'}
                    value={singleChannel === 'email' ? singleMessageForm.email : singleMessageForm.phone}
                    onChange={(e) => setSingleMessageForm({
                      ...singleMessageForm,
                      [singleChannel === 'email' ? 'email' : 'phone']: e.target.value
                    })}
                    placeholder={
                      singleChannel === 'email'
                        ? 'Enter email address (e.g., john@example.com)'
                        : 'Enter phone number (e.g., +18339027623)'
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  />
                ) : (
                  <>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <Search className="text-gray-400" size={18} />
                      </div>
                      <input
                        type="text"
                        value={singleMessageForm.searchTerm}
                        onChange={(e) => setSingleMessageForm({ ...singleMessageForm, searchTerm: e.target.value, contactId: null })}
                        placeholder={`Search by name or ${singleChannel === 'email' ? 'email' : 'phone'}...`}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                      />
                    </div>
                    
                    {singleMessageForm.searchTerm && filteredContacts.length > 0 && !selectedContact && (
                      <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-lg max-h-60 overflow-y-auto">
                        {filteredContacts.map(contact => (
                          <button
                            key={contact.id}
                            onClick={() => setSingleMessageForm({
                              ...singleMessageForm,
                              contactId: contact.id,
                              searchTerm: singleChannel === 'email' ? contact.email : contact.phone
                            })}
                            className="w-full px-4 py-3 text-left hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {contact.firstName || contact.first_name || ''} {contact.lastName || contact.last_name || ''}
                            </div>
                            <div className="text-sm text-gray-600">{singleChannel === 'email' ? contact.email : contact.phone}</div>
                          </button>
                        ))}
                      </div>
                    )}

                    {selectedContact && (
                      <div className="mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {selectedContact.firstName || selectedContact.first_name || ''} {selectedContact.lastName || selectedContact.last_name || ''}
                          </div>
                          <div className="text-sm text-gray-600">{singleChannel === 'email' ? selectedContact.email : selectedContact.phone}</div>
                        </div>
                        <button
                          onClick={() => setSingleMessageForm({ ...singleMessageForm, contactId: null, searchTerm: '' })}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  onClick={handleSendSingleMessage}
                  disabled={
                    !singleMessageForm.templateId ||
                    sendingSingleMessage ||
                    (useDirectRecipient
                      ? (singleChannel === 'email'
                          ? !singleMessageForm.email || !isValidEmail(singleMessageForm.email)
                          : !singleMessageForm.phone || !isValidPhone(singleMessageForm.phone))
                      : !singleMessageForm.contactId)
                  }
                  className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sendingSingleMessage ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send {singleChannel === 'email' ? 'Email' : 'SMS'}
                    </>
                  )}
                </button>
                {singleChannel === 'sms' && (
                  <button
                    onClick={handleSendSingleTest}
                    disabled={
                      testSmsSending ||
                      !singleMessageForm.templateId ||
                      (useDirectRecipient
                        ? !singleMessageForm.phone || !isValidPhone(singleMessageForm.phone)
                        : !singleMessageForm.contactId)
                    }
                    className="w-full px-4 py-3 bg-white border-2 border-purple-300 text-purple-700 rounded-xl font-semibold hover:bg-purple-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {testSmsSending ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <MailCheck size={18} />
                        Send Test SMS
                      </>
                    )}
                  </button>
                )}
              </div>

              {(!singleMessageForm.templateId || (useDirectRecipient ? (singleChannel === 'email' ? !singleMessageForm.email || !isValidEmail(singleMessageForm.email) : !singleMessageForm.phone || !isValidPhone(singleMessageForm.phone)) : !singleMessageForm.contactId)) && (
                <div className="text-xs text-amber-600 text-center">
                  {!singleMessageForm.templateId && 'Select template • '}
                  {useDirectRecipient
                    ? (singleChannel === 'email'
                        ? (!singleMessageForm.email ? 'Enter email address' : !isValidEmail(singleMessageForm.email) && 'Enter valid email address')
                        : (!singleMessageForm.phone ? 'Enter phone number' : !isValidPhone(singleMessageForm.phone) && 'Enter valid phone number'))
                    : !singleMessageForm.contactId && 'Select contact'}
                </div>
              )}
              {singleChannel === 'sms' && (
                <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-3 text-xs text-purple-800">
                  SMS sends require a Twilio sender number or messaging service to be configured in Settings. Include opt-out language like STOP in marketing copy.
                </div>
              )}
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Active Campaigns ({activeCampaigns.length})</h2>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${inProgressCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {inProgressCount} in progress
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${queuedCount > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}`}>
                  {queuedCount} queued
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${scheduledCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                  {scheduledCount} scheduled
                </span>
              </div>
            </div>
            
            <div className="space-y-3">
              {activeCampaigns.map(campaign => {
                const openRate = campaign.channel === 'email' && campaign.delivered > 0 
                  ? ((campaign.opened / campaign.delivered) * 100).toFixed(1) 
                  : 0;
                const isExpanded = expandedCampaign === campaign.id;
                const statusClasses = {
                  completed: 'bg-green-100 text-green-800',
                  in_progress: 'bg-indigo-100 text-indigo-800',
                  queued: 'bg-blue-100 text-blue-800',
                  scheduled: 'bg-amber-100 text-amber-800'
                };
                const statusLabels = {
                  completed: 'Completed',
                  in_progress: 'Sending',
                  queued: 'Queued',
                  scheduled: 'Scheduled'
                };
                const statusClassName = statusClasses[campaign.status] || 'bg-gray-100 text-gray-800';
                const statusLabel = statusLabels[campaign.status] || campaign.status;
                const totalMessages = campaign.sent || campaign.recipientCount || 0;
                const processed = campaign.processedCount || 0;
                const progressPercent = totalMessages > 0 ? Math.round((processed / totalMessages) * 100) : 0;
                const statusHint = campaign.status === 'scheduled'
                  ? (campaign.scheduledDate ? `Scheduled for ${new Date(campaign.scheduledDate).toLocaleString()}` : 'Scheduled')
                  : campaign.status === 'completed'
                    ? (campaign.completedAt ? `Completed ${new Date(campaign.completedAt).toLocaleString()}` : 'Completed')
                    : `Sending up to ${campaign.throttle} messages/hour`;

                return (
                  <div key={campaign.id} className="bg-gradient-to-r from-gray-50 to-indigo-50 rounded-xl hover:shadow-md transition-all duration-300">
                    <div 
                      className="p-5 cursor-pointer"
                      onClick={() => setExpandedCampaign(isExpanded ? null : campaign.id)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`p-2 ${campaign.channel === 'email' ? 'bg-indigo-100' : 'bg-purple-100'} rounded-lg`}>
                            {campaign.channel === 'email' 
                              ? <Mail className="text-indigo-600" size={18} /> 
                              : <MessageSquare className="text-purple-600" size={18} />
                            }
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{campaign.name}</div>
                            <div className="text-sm text-gray-600">{campaign.template} • {campaign.recipientCount} recipients</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusClassName}`}>
                            {statusLabel}
                          </span>
                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-gray-900">{campaign.sent}</div>
                          <div className="text-xs text-gray-600">Sent</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">{campaign.delivered}</div>
                          <div className="text-xs text-gray-600">Delivered</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-indigo-600">
                            {campaign.channel === 'email' ? campaign.opened : campaign.replied}
                          </div>
                          <div className="text-xs text-gray-600">{campaign.channel === 'email' ? 'Opened' : 'Replied'}</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-purple-600">
                            {campaign.channel === 'email'
                              ? `${openRate}%`
                              : `${campaign.delivered > 0 ? ((campaign.replied / campaign.delivered) * 100).toFixed(1) : '0.0'}%`}
                          </div>
                          <div className="text-xs text-gray-600">{campaign.channel === 'email' ? 'Open Rate' : 'Reply Rate'}</div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>{processed}/{totalMessages} processed</span>
                          <span>{progressPercent}%</span>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${campaign.status === 'completed' ? 'bg-green-500' : 'bg-indigo-500'}`}
                            style={{ width: `${Math.min(progressPercent, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{statusHint}</div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-5 pb-5 border-t border-gray-200 pt-4 space-y-3">
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div className="p-3 bg-white rounded-lg">
                            <div className="text-gray-600 mb-1">Bounced</div>
                            <div className="text-lg font-bold text-red-600">{campaign.bounced}</div>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <div className="text-gray-600 mb-1">Unsubscribed</div>
                            <div className="text-lg font-bold text-amber-600">{campaign.unsubscribed}</div>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <div className="text-gray-600 mb-1">Complained</div>
                            <div className="text-lg font-bold text-red-600">{campaign.complained}</div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedCampaign(campaign.id);
                              setCurrentPage('reports');
                            }}
                            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center justify-center gap-2"
                          >
                            <Send size={16} />
                            View Report
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              exportCampaignReport(campaign);
                            }}
                            className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-300 flex items-center justify-center gap-2"
                          >
                            <Download size={16} />
                            Export CSV
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCampaign(campaign.id);
                            }}
                            disabled={campaignDeletingId === campaign.id}
                            className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {campaignDeletingId === campaign.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            {campaignDeletingId === campaign.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {activeCampaigns.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Send className="mx-auto mb-3 text-gray-400" size={48} />
                  <p className="text-lg font-semibold mb-1">No active campaigns</p>
                  <p className="text-sm">Completed campaigns are available in reports.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Campaign Creation Form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 sticky top-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Campaign</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Campaign Name *</label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  placeholder="e.g., Summer Sale 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Channel *</label>
                <select
                  value={campaignForm.channel}
                  onChange={(e) => setCampaignForm({
                    ...campaignForm,
                    channel: e.target.value,
                    templateId: null,
                    emailTemplateId: null,
                    smsTemplateId: null
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="multi">Email + SMS</option>
                </select>
              </div>

              {campaignForm.channel !== 'multi' ? (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Template *</label>
                  <select
                    value={campaignForm.templateId || ''}
                    onChange={(e) => setCampaignForm({ ...campaignForm, templateId: e.target.value || null })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  >
                    <option value="">Select a template</option>
                    {templates.filter(t => t.type === campaignForm.channel).map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}{template.type === 'email' && (template.fromName || template.fromEmail) ? ` · ${template.fromName || template.fromEmail}` : ''}
                      </option>
                    ))}
                  </select>
                  {templates.filter(t => t.type === campaignForm.channel).length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No templates available for this channel</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Template *</label>
                    <select
                      value={campaignForm.emailTemplateId || ''}
                      onChange={(e) => setCampaignForm({ ...campaignForm, emailTemplateId: e.target.value || null })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select an email template</option>
                      {templates.filter(t => t.type === 'email').map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name}{template.fromName || template.fromEmail ? ` · ${template.fromName || template.fromEmail}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">SMS Template *</label>
                    <select
                      value={campaignForm.smsTemplateId || ''}
                      onChange={(e) => setCampaignForm({ ...campaignForm, smsTemplateId: e.target.value || null })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select an SMS template</option>
                      {templates.filter(t => t.type === 'sms').map(template => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Recipients</label>
                <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Users className="text-indigo-600" size={24} />
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{selectedContacts.size}</div>
                      <div className="text-sm text-gray-600">contacts selected</div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg bg-white/70 px-3 py-2">
                      <div className="font-semibold text-gray-900">
                        {campaignForm.channel === 'multi' ? emailReadyCount : availableRecipientCount}
                      </div>
                      <div className="text-gray-600">
                        {campaignForm.channel === 'email' ? 'ready for email' : campaignForm.channel === 'sms' ? 'ready for SMS' : 'ready for email'}
                      </div>
                    </div>
                    <div className="rounded-lg bg-white/70 px-3 py-2">
                      <div className="font-semibold text-gray-900">
                        {campaignForm.channel === 'multi' ? smsReadyCount : unavailableRecipientCount}
                      </div>
                      <div className="text-gray-600">
                        {campaignForm.channel === 'multi'
                          ? 'ready for SMS'
                          : `missing ${campaignForm.channel === 'email' ? 'email' : 'phone'}`}
                      </div>
                    </div>
                  </div>
                  {campaignForm.channel === 'multi' && (
                    <div className="mt-2 rounded-lg bg-white/70 px-3 py-2 text-xs">
                      <div className="font-semibold text-gray-900">{bothReadyCount}</div>
                      <div className="text-gray-600">ready for both channels</div>
                    </div>
                  )}
                </div>
                {selectedContacts.size === 0 && (
                  <button
                    onClick={() => setCurrentPage('contacts')}
                    className="w-full mt-2 px-4 py-2 bg-white border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors duration-300 text-sm font-semibold"
                  >
                    Select Contacts
                  </button>
                )}
                {selectedContacts.size > 0 && unavailableRecipientCount > 0 && campaignForm.channel !== 'multi' && (
                  <p className="mt-2 text-xs text-amber-600">
                    {unavailableRecipientCount} selected contact(s) will be skipped because they do not have a valid {campaignForm.channel === 'email' ? 'email address' : 'phone number'}.
                  </p>
                )}
                {selectedContacts.size > 0 && campaignForm.channel === 'multi' && (
                  <p className="mt-2 text-xs text-amber-600">
                    The same selected audience will be used for both channels. Contacts missing email will be skipped from the email campaign, and contacts missing phone will be skipped from the SMS campaign.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Send Speed</label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setCampaignForm({ ...campaignForm, sendAllAtOnce: true })}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all duration-200 ${
                      campaignForm.sendAllAtOnce
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      campaignForm.sendAllAtOnce ? 'border-indigo-500' : 'border-gray-400'
                    }`}>
                      {campaignForm.sendAllAtOnce && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Send all at once</div>
                      <div className="text-xs text-gray-500">Blast the entire list immediately</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCampaignForm({ ...campaignForm, sendAllAtOnce: false })}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all duration-200 ${
                      !campaignForm.sendAllAtOnce
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      !campaignForm.sendAllAtOnce ? 'border-indigo-500' : 'border-gray-400'
                    }`}>
                      {!campaignForm.sendAllAtOnce && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Controlled rate</div>
                      <div className="text-xs text-gray-500">Spread sends for better deliverability</div>
                    </div>
                  </button>
                  {!campaignForm.sendAllAtOnce && (
                    <div className="pt-1">
                      <input
                        type="number"
                        value={campaignForm.throttle}
                        onChange={(e) => setCampaignForm({ ...campaignForm, throttle: Math.max(1, parseInt(e.target.value, 10) || 500) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                        min="1"
                        max="10000"
                        placeholder="Messages per hour"
                      />
                      <p className="text-xs text-gray-500 mt-1">Messages per hour — 100–500 recommended for best deliverability</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="sendNow"
                  checked={campaignForm.sendNow}
                  onChange={(e) => setCampaignForm({ ...campaignForm, sendNow: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="sendNow" className="text-sm font-semibold text-gray-700 cursor-pointer">
                  Send immediately
                </label>
              </div>

              {!campaignForm.sendNow && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Schedule Date & Time</label>
                  <input
                    type="datetime-local"
                    value={campaignForm.scheduledDate || ''}
                    onChange={(e) => setCampaignForm({ ...campaignForm, scheduledDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
              )}

              {campaignForm.channel === 'email' && campaignForm.templateId && (
                <button
                  onClick={sendTestEmail}
                  disabled={testEmailSending || !campaignForm.templateId}
                  className="w-full px-4 py-2 bg-white border-2 border-indigo-300 text-indigo-700 rounded-lg font-semibold hover:bg-indigo-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {testEmailSending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MailCheck size={16} />
                      Send Test Email
                    </>
                  )}
                </button>
              )}

              {campaignForm.channel === 'sms' && campaignForm.templateId && (
                <button
                  onClick={() => {
                    const contact = selectedRecipientList.find((item) => item?.phone);
                    if (!contact) return;
                    sendTestSms(campaignForm.templateId, contact.id, null);
                  }}
                  disabled={testSmsSending || !campaignForm.templateId || availableRecipientCount === 0 || !smsReady}
                  className="w-full px-4 py-2 bg-white border-2 border-purple-300 text-purple-700 rounded-lg font-semibold hover:bg-purple-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {testSmsSending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <MessageSquare size={16} />
                      Send Test SMS
                    </>
                  )}
                </button>
              )}

              {campaignForm.channel === 'sms' && !smsReady && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  SMS is not fully configured yet. Add Twilio credentials and a sender number or messaging service in Settings before sending SMS campaigns.
                </div>
              )}

              {campaignForm.channel === 'multi' && !smsReady && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                  Multichannel sends require SMS to be configured in Settings because this flow creates both the email campaign and the SMS campaign together.
                </div>
              )}

              <button
                onClick={createCampaign}
                disabled={
                  !campaignForm.name ||
                  (campaignForm.channel === 'multi'
                    ? (!campaignForm.emailTemplateId || !campaignForm.smsTemplateId)
                    : !campaignForm.templateId) ||
                  selectedContacts.size === 0 ||
                  (campaignForm.channel === 'multi'
                    ? (emailReadyCount === 0 || smsReadyCount === 0)
                    : availableRecipientCount === 0) ||
                  ((campaignForm.channel === 'sms' || campaignForm.channel === 'multi') && !smsReady)
                }
                className="w-full px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Send size={18} />
                {campaignForm.sendNow
                  ? `Send ${campaignForm.channel === 'email' ? 'Email' : campaignForm.channel === 'sms' ? 'SMS' : 'Email + SMS'} Campaign`
                  : `Schedule ${campaignForm.channel === 'email' ? 'Email' : campaignForm.channel === 'sms' ? 'SMS' : 'Email + SMS'} Campaign`}
              </button>

              {(!campaignForm.name ||
                (campaignForm.channel === 'multi'
                  ? (!campaignForm.emailTemplateId || !campaignForm.smsTemplateId)
                  : !campaignForm.templateId) ||
                selectedContacts.size === 0 ||
                (campaignForm.channel === 'multi' ? (emailReadyCount === 0 || smsReadyCount === 0) : availableRecipientCount === 0) ||
                ((campaignForm.channel === 'sms' || campaignForm.channel === 'multi') && !smsReady)) && (
                <div className="text-xs text-amber-600 text-center">
                  {!campaignForm.name && 'Enter campaign name • '}
                  {campaignForm.channel === 'multi'
                    ? (!campaignForm.emailTemplateId && 'Select email template • ') || (!campaignForm.smsTemplateId && 'Select SMS template • ')
                    : !campaignForm.templateId && 'Select template • '}
                  {selectedContacts.size === 0 && 'Select recipients'}
                  {selectedContacts.size > 0 && campaignForm.channel !== 'multi' && availableRecipientCount === 0 && `No contacts have a valid ${campaignForm.channel === 'email' ? 'email address' : 'phone number'}`}
                  {selectedContacts.size > 0 && campaignForm.channel === 'multi' && emailReadyCount === 0 && 'No contacts have a valid email address'}
                  {selectedContacts.size > 0 && campaignForm.channel === 'multi' && smsReadyCount === 0 && `${emailReadyCount === 0 ? ' • ' : ''}No contacts have a valid phone number`}
                  {(campaignForm.channel === 'sms' || campaignForm.channel === 'multi') && !smsReady && ' • Configure Twilio sender in Settings'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignsPage;

